import {
  MEAL_PLAN_DAY_ORDER,
  PLANNER_DAY_LONG,
  PLANNER_DAY_LETTER,
  calendarMealShortLabel,
} from '../../utils/mealPlanHelpers.js'
import { isNightOffMeal, nightOffLabel } from '../../utils/nightOff.js'
import { textColorForSwatch } from '../../utils/plannerSwatchContrast.js'
import {
  PLAN_COOK_GOAL_N,
  countPlanCookProgress,
  remainingNightsInPlanWeek,
  isPlanWeekOrientation,
  resolvePlanCookStage,
  messageForPlanCook,
  planCookDayLabel,
} from '../../utils/planCookProgress.js'
import './planner.css'

function PcpDayTicks({ remainingNights }) {
  const past = Math.max(0, 7 - remainingNights)
  return (
    <div className="pcp-day-ticks" aria-hidden="true">
      {Array.from({ length: 7 }).map((_, i) => (
        <span
          key={i}
          className={`pcp-day-tick${i < past ? ' is-past' : ' is-remaining'}`}
        />
      ))}
    </div>
  )
}

function PcpMeter({ label, value, goalN, bold, compact }) {
  const pct = Math.min(100, Math.round((value / Math.max(goalN, 1)) * 100))
  return (
    <div className={`pcp-meter${bold ? ' is-bold' : ' is-muted'}${compact ? ' is-compact' : ''}`}>
      <div className="pcp-meter-label-row">
        <span className="pcp-meter-label">{label}</span>
        <span className="pcp-meter-value">
          {value}
          {' of '}
          {goalN}
        </span>
      </div>
      <div
        className="pcp-meter-track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={goalN}
        aria-valuenow={value}
        aria-label={`${label}: ${value} of ${goalN}`}
      >
        <div className="pcp-meter-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

function stripNightCopy(meal) {
  if (!meal) {
    return { kind: 'open', label: '\u2013', swatch: null, color: null }
  }
  if (isNightOffMeal(meal)) {
    return {
      kind: 'night-off',
      label: nightOffLabel(meal.nightOffType),
      swatch: null,
      color: null,
    }
  }
  const swatch = meal.swatch || null
  return {
    kind: 'planned',
    label: calendarMealShortLabel(meal.name, { shortLabel: meal.shortLabel }) || meal.name || 'Dinner',
    swatch,
    color: swatch ? textColorForSwatch(swatch) : null,
  }
}

export function PlanWeekStrip({ assignedByDay, todayKey, pastDayKeys, compact }) {
  const past = new Set(pastDayKeys || [])
  return (
    <ol className="pcs-strip" aria-label="This week">
      {MEAL_PLAN_DAY_ORDER.map((dayKey) => {
        const meal = assignedByDay?.[dayKey] || null
        const night = stripNightCopy(meal)
        const isToday = todayKey != null && dayKey === todayKey
        const isPast = Boolean(meal?.isPast) || past.has(dayKey)
        const longLabel = PLANNER_DAY_LONG[dayKey] || dayKey
        const dayMark = compact ? (PLANNER_DAY_LETTER[dayKey] || dayKey[0]) : longLabel
        const spoken = meal
          ? `${longLabel}: ${isNightOffMeal(meal) ? nightOffLabel(meal.nightOffType) : meal.name}`
          : `${longLabel}: open`
        const barStyle = night.swatch
          ? { background: night.swatch, color: night.color }
          : undefined
        return (
          <li
            key={dayKey}
            className={['pcs-day', isPast ? 'is-past' : ''].filter(Boolean).join(' ')}
            title={meal ? `${longLabel}: ${meal.name}` : `${longLabel}: open`}
          >
            <span className={['pcs-dow', isToday ? 'is-today' : ''].filter(Boolean).join(' ')} aria-hidden="true">
              {dayMark}
            </span>
            <span
              className={[
                'pcs-bar',
                `is-${night.kind}`,
                isToday ? 'is-today' : '',
              ].filter(Boolean).join(' ')}
              style={barStyle}
              aria-hidden="true"
            >
              <span className="pcs-bar-label">{night.label}</span>
            </span>
            <span className="sr-only">
              {spoken}
              {isToday ? ', today' : ''}
              {isPast ? ', past' : ''}
            </span>
          </li>
        )
      })}
    </ol>
  )
}

/**
 * Plan → Cook Progress coaching card.
 *
 * `showWeekStrip` merges the week strip into this card and shows one
 * stage-aware meter (Planned while the stage is still planning, Cooked after).
 */
export default function PlanCookProgress({
  assigned = [],
  weekStartIso = null,
  accountCreatedAt = null,
  goalN = PLAN_COOK_GOAL_N,
  density = 'desktop',
  showWeekStrip = false,
  assignedByDay = null,
  todayKey = null,
  pastDayKeys = null,
}) {
  if (!weekStartIso) return null

  const { planned, cooked } = countPlanCookProgress(assigned)
  const compact = density === 'mobile'

  const remainingNights = remainingNightsInPlanWeek(weekStartIso)
  const isOrientation = isPlanWeekOrientation({ accountCreatedAt, weekStartIso })
  const stage = isOrientation
    ? 'orientation'
    : resolvePlanCookStage({ planned, cooked, remainingNights, goalN })
  const msg = messageForPlanCook({
    stage,
    planned,
    cooked,
    goalN,
    remainingNights,
    isOrientation,
  })
  const dayLabel = planCookDayLabel(weekStartIso)
  const eyebrow = isOrientation
    ? 'First week · orientation'
    : `${dayLabel ? `${dayLabel} · ` : ''}${remainingNights} night${remainingNights === 1 ? '' : 's'} left`
  const meterLabel = msg.primary === 'cook' ? 'Cooked' : 'Planned'
  const meterValue = msg.primary === 'cook' ? cooked : planned

  return (
    <div
      className={[
        'pcp-card',
        compact ? 'pcp-card--mobile' : 'pcp-card--desktop',
        showWeekStrip ? 'pcp-card--strip' : '',
        msg.flare ? 'pcp-card--flare' : '',
      ].filter(Boolean).join(' ')}
      data-pcp-stage={stage}
    >
      <div className="pcp-top-row">
        <span className="eyebrow pcp-eyebrow">{eyebrow}</span>
        {!showWeekStrip && !isOrientation && <PcpDayTicks remainingNights={remainingNights} />}
      </div>

      <div className={`pcp-headline${compact ? ' pcp-headline--mobile' : ''}`}>
        {msg.headline}
      </div>

      {showWeekStrip && (
        <PlanWeekStrip
          assignedByDay={assignedByDay}
          todayKey={todayKey}
          pastDayKeys={pastDayKeys}
          compact={compact}
        />
      )}

      {!(showWeekStrip && msg.showMeters) && (
        <div className="pcp-detail">{msg.detail}</div>
      )}

      {msg.showMeters && showWeekStrip && (
        <div className="pcp-footer">
          <PcpMeter
            label={meterLabel}
            value={meterValue}
            goalN={goalN}
            bold
            compact={compact}
          />
          {!compact && <div className="pcp-detail">{msg.detail}</div>}
        </div>
      )}

      {msg.showMeters && !showWeekStrip && (
        <div className={`pcp-meters${compact ? ' pcp-meters--stack' : ''}`}>
          <PcpMeter
            label="Planned"
            value={planned}
            goalN={goalN}
            bold={msg.primary === 'plan'}
            compact={compact}
          />
          <PcpMeter
            label="Cooked"
            value={cooked}
            goalN={goalN}
            bold={msg.primary === 'cook'}
            compact={compact}
          />
        </div>
      )}

      {msg.whisper && (
        <div className="pcp-whisper">{msg.whisper}</div>
      )}
    </div>
  )
}
